/**
 * The Settings rail (round9/design-pass — Direction A).
 *
 * `/settings/*` is ONE page (SettingsPage) with one form and one Save; the
 * rail switches which section of that form is on screen and the URL records
 * it, so a section is linkable and Back works. The Embed page keeps its own
 * route and its own Save (different PUT keys, different validation) but sits
 * on the same rail so a builder finds every "configure my business" surface
 * in one place. Templates is deliberately NOT here: it is a top-level
 * destination in the primary nav.
 *
 * Kept as a plain module (no React) so SettingsPage, the rail component and
 * tests can all import it without a render.
 */

export type SettingsSectionKey = "rate-card" | "quoting" | "engineering" | "embed";

export interface SettingsSection {
  key: SettingsSectionKey;
  label: string;
  path: string;
  /** One line under the section's h1. */
  hint: string;
}

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    key: "rate-card",
    label: "Rate card & pricing",
    path: "/settings/rate-card",
    hint: "What a homeowner sees as a cost estimate is computed from these numbers.",
  },
  {
    key: "quoting",
    label: "Quoting",
    path: "/settings/quoting",
    hint: "Controls whether, and how, homeowners see a cost estimate.",
  },
  {
    key: "engineering",
    label: "Engineering rules",
    path: "/settings/engineering",
    hint: "The structural conventions your instance designs with. Only the account owner can change these.",
  },
  {
    key: "embed",
    label: "Embed",
    path: "/embed",
    hint: "What your embedded designer offers homeowners.",
  },
];

/** Sections rendered by SettingsPage itself (everything but Embed). */
export type SettingsPageSectionKey = Exclude<SettingsSectionKey, "embed">;

export const DEFAULT_SETTINGS_SECTION: SettingsPageSectionKey = "rate-card";

/**
 * `/settings/quoting` → "quoting"; `/settings`, `/settings/` and anything
 * unknown → the default. `/rate-card` (the pre-design-pass route) is a
 * redirect in main.tsx, not a section here.
 */
export function settingsSectionFromPath(pathname: string): SettingsPageSectionKey {
  const m = /^\/settings\/([^/]+)/.exec(pathname);
  const key = m?.[1];
  if (key === "quoting" || key === "engineering" || key === "rate-card") return key;
  return DEFAULT_SETTINGS_SECTION;
}

export function settingsSection(key: SettingsSectionKey): SettingsSection {
  const found = SETTINGS_SECTIONS.find((s) => s.key === key);
  if (!found) throw new Error(`unknown settings section: ${key}`);
  return found;
}
